import { ICliJob } from 'cli/types';
import { mapRecordFromServer, mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, GetOneResponse } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';
import { mapQuotePartListExtraDataFromServer } from 'quotes/mappers/quotePartList';
import { mapTradeAgreementLinesFromServer } from 'quotes/mappers/tradeAgreement';
import {
  IApplySelectedSpaToLinesRequest,
  IApplySelectedSpaToLinesResponse,
  ICustomerQuotePart,
  ICustomerQuotePartListRequestPayload,
  ICustomerQuotePartReorderData,
  IQuotePartExtraData,
  IQuotePartPricingGuidance,
  IRemoveQuotePartNotesByTypePayload,
  IResetPricingPayload,
  ISelectedSpaPricesRequest,
  ITradeAgreementLine,
  IValidatePart,
  IVendorQuotePart,
  IVendorQuotePartListRequestPayload,
  TTradeAgreementsRequest,
} from 'quotes/types';

export const getCustomerQuoteParts = async (
  requestPayload: ICustomerQuotePartListRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<ICustomerQuotePart, IQuotePartExtraData>> => {
  const { data } = await partfinitiClient.get(
    `/quote/customer/parts?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  const quoteParts = data.data.map((quotePart: ICustomerQuotePart) => ({
    ...quotePart,
    deleted_status: {
      deleted: quotePart.deleted,
      omitted: quotePart.omitted,
    },
  }));

  return mapRecordsFromServer<ICustomerQuotePart, IQuotePartExtraData>(
    quoteParts,
    data.recordsFiltered,
    data.totals,
    mapQuotePartListExtraDataFromServer(data)
  );
};

export const getVendorQuoteParts = async (
  requestPayload: IVendorQuotePartListRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IVendorQuotePart>> => {
  const { data } = await partfinitiClient.get(
    `/quote/vendor/parts?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  const quoteParts = data.data.map((quotePart: ICustomerQuotePart) => ({
    ...quotePart,
    deleted_status: {
      deleted: quotePart.deleted,
      omitted: quotePart.omitted,
    },
  }));

  return mapRecordsFromServer<IVendorQuotePart>(quoteParts, data.recordsFiltered, data.totals);
};

export const getQuotePartPricingGuidance = async (quotePartId: number, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/quote/part/pricing-guidance?${getQueryStringToSnakeCase({ quotePartId })}`,
    signal
  );

  return mapRecordsFromServer<IQuotePartPricingGuidance>(data.pricing_guidance);
};

// Saved lines (quoteId + quotePartIds) or a transient line built from the form values.
export const getTradeAgreements = async (
  requestPayload: TTradeAgreementsRequest,
  signal?: AbortSignal
): Promise<GetManyResponse<ITradeAgreementLine>> => {
  const { data } = await partfinitiClient.get(
    `/quote/part/trade-agreements?${getQueryStringToSnakeCase(requestPayload, {
      removeNullValues: true,
    })}`,
    signal
  );

  const { records, ...rest } = mapRecordsFromServer<ITradeAgreementLine>(data.lines);

  return { ...rest, records: mapTradeAgreementLinesFromServer(records) };
};

export const applySelectedSpaToLines = async (
  data: IApplySelectedSpaToLinesRequest
): Promise<ICliJob> =>
  (
    (await partfinitiClient.postForm(
      '/quote/part/apply-selected-spa-to-lines',
      data
    )) as IApplySelectedSpaToLinesResponse
  ).cliJob;

export const upsertQuotePart = async (data) =>
  partfinitiClient.postForm('/quote/part/save', data, { includeNullValues: true });

export const changeQuotePart = async (data) =>
  partfinitiClient.postForm('/quote/part/change-part', data, { includeNullValues: true });

export const upsertQuotePartsAsync = async (data: ICustomerQuotePartReorderData, options = {}) =>
  (await partfinitiClient.postForm('/quote/part/save-bulk', data, options)).cliJob;

export const upsertQuotePartNotes = async (data) =>
  (await partfinitiClient.postForm('/quote/part-note/save-bulk', data)).cliJob;

export const removeQuotePartNotesByType = async (data: IRemoveQuotePartNotesByTypePayload) =>
  (await partfinitiClient.postForm('/quote/part-note/remove-by-type-bulk', data)).cliJob;

export const upsertQuotePartNote = async (data) =>
  await partfinitiClient.postForm('/quote/part-note/save', data);

export const removeQuotePartNote = async (data) =>
  await partfinitiClient.postForm('/quote/part-note/remove', data);

export const getLeadTime = async (quotePartId: number, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/quote/part/get-lead-time?${getQueryStringToSnakeCase({ quotePart: { id: quotePartId } })}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const removeQuoteParts = async (data) =>
  (await partfinitiClient.postForm('/quote/part/remove', data)).cliJob;

export const resetPricing = async (data: IResetPricingPayload) =>
  (await partfinitiClient.postForm('/quote/part/reset-pricing', data)).cliJob;

export const resetExpiredCosting = async (data) =>
  await partfinitiClient.postForm('/quote/part/reset-expired-costing', data);

export const moveTo = async (data) =>
  (await partfinitiClient.postForm('/quote/customer/move-to', data)).cliJob;

export const duplicate = async (data) =>
  (await partfinitiClient.postForm('/quote/part/duplicate-bulk', data)).cliJob;

export const applyCostDiscount = async (data) =>
  (await partfinitiClient.postForm('/quote/part/apply-cost-discount-in-bulk', data)).cliJob;

export const validate = async (
  number: string,
  manufacturerId: number,
  vendorNumber: string,
  quoteId: number,
  unverifiedOnly = false,
  pricingDate?: string,
  pricingStrategy?: string,
  signal?: AbortSignal
): Promise<GetOneResponse<IValidatePart>> => {
  const { data } = await partfinitiClient.get(
    `/quote/part/validate-part?${getQueryStringToSnakeCase(
      {
        number,
        manufacturerId,
        vendorNumber,
        quoteId,
        unverifiedOnly: unverifiedOnly ? 1 : 0,
        pricingDate,
        pricingStrategy,
      },
      { removeNullValues: true }
    )}`,
    signal
  );

  return mapRecordFromServer<IValidatePart>(data, data.success);
};

export const calculatePrices = async ({
  quoteId,
  number,
  manufacturerId,
  vendorNumber,
  accountsPayableId,
  quantity,
  uom,
  purchaseCost,
  shipType,
  shippingLocationId,
  pricingDate,
  pricingStrategy,
  ignoreExternalBestCost,
  prioritizeLegacyCost,
  fetchReplacementStandardCost,
  lineReference,
  isCostingSelectedSpa,
  costSource,
  signal,
}: {
  quoteId: number;
  number: string;
  manufacturerId: number;
  vendorNumber: string;
  accountsPayableId?: string;
  quantity: number;
  uom: string;
  purchaseCost?: number;
  shipType?: string;
  shippingLocationId?: number;
  pricingDate?: string;
  pricingStrategy?: string;
  ignoreExternalBestCost?: number;
  prioritizeLegacyCost?: number;
  fetchReplacementStandardCost?: number;
  lineReference?: number;
  signal?: AbortSignal;
} & ISelectedSpaPricesRequest) => {
  const payload = {
    quoteId,
    number,
    manufacturerId,
    vendorNumber,
    accountsPayableId,
    quantity,
    uom,
    shipType,
    shippingLocationId,
    pricingDate,
    pricingStrategy,
    ignoreExternalBestCost,
    prioritizeLegacyCost,
    lineReference,
  };

  if (purchaseCost || purchaseCost === 0) {
    payload['purchaseCost'] = purchaseCost;
  }

  // Opt-in only. The BE casts this off the query string with (bool), so "false"/"0" would read
  // truthy — the key must be entirely ABSENT when off (never 0/false/undefined on the wire). Add
  // it solely when we actually want the BE to derive external_replacement_standard_cost.
  if (fetchReplacementStandardCost) {
    payload['fetchReplacementStandardCost'] = fetchReplacementStandardCost;
  }

  // Only with a held pick; any other preview's request is unchanged.
  if (isCostingSelectedSpa && costSource) {
    payload['isCostingSelectedSpa'] = isCostingSelectedSpa;
    payload['costSource'] = costSource;
  }

  const { data } = await partfinitiClient.get(
    `/quote/part/prices?${getQueryStringToSnakeCase(payload, { removeNullValues: true })}`,
    signal
  );

  return mapRecordFromServer(data.prices, data.success);
};

export const addToPricingProfile = async (data) =>
  (await partfinitiClient.postForm('/quote/part/add-to-pricing-profile', data)).cliJob;

export const getDiscountPartsFixed = async (filters) => {
  const { data } = await partfinitiClient.get(
    `/quote/part/get-discount-parts-fixed?${getQueryStringToSnakeCase(filters)}`
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const addPnbVariations = async (data) =>
  partfinitiClient.postForm('/quote/part/add-pnb-variations', data);

export const setPriceBySource = async (data) =>
  partfinitiClient.postForm('/quote/part/set-price-by-source', data);
